import { z } from 'zod';
import {
  ClassActiveSchema,
  ClassAllowMakeupsSchema,
  ClassClassIdSchema,
  ClassCourseSchema,
  ClassDaySchema,
  ClassLocationSchema,
  ClassMarkerNameSchema,
  ClassMarkerSchema,
  ClassNotesSchema,
  ClassPermissionGroupSchema,
  ClassRemindBreaksSchema,
  ClassRoomSchema,
  ClassSchoolTagSchema,
  ClassSlackChannelNameSchema,
  ClassSlackIdSchema,
  ClassStreamSchema,
  ClassStudentCapSchema,
  ClassStudentCountSchema,
  ClassStudentsSchema,
  ClassSubjectSchema,
  ClassTagsSchema,
  ClassTeacherNameSchema,
  ClassTeacherSchema,
  ClassTimeSchema,
  ClassWaitlistSchema,
  DBClassClassIdSchema,
  DBClassSchema,
} from '../classes/schemas';
import { SubjectSchema, UnixTimestampSchema } from '../common/schemas';
import {
  DBLessonEnrolmentSchema,
  LessonEnrolmentAttendanceNotifiedSchema,
  LessonEnrolmentAttendanceTypeSchema,
  LessonEnrolmentBillingTypeSchema,
  LessonEnrolmentClassIdSchema,
  LessonEnrolmentCourseSchema,
  LessonEnrolmentEnrolmentMethodSchema,
  LessonEnrolmentHomeworkSchema,
  LessonEnrolmentLessonSchema,
  LessonEnrolmentPresentSchema,
  LessonEnrolmentRollMarkedSchema,
  LessonEnrolmentSeparatelyBilledSchema,
  LessonEnrolmentStartDatetimeSchema,
  LessonEnrolmentStreamSchema,
  LessonEnrolmentStudentSchema,
} from '../lessonEnrolments/schemas';
import { DBStaffStaffIdSchema } from '../staff/schemas';
import {
  DBStudentCourseStateStateSchema,
  DBStudentStateSchema,
  DBStudentStudentIdSchema,
} from '../students/schemas';
import { StudentActivityReasonCategory, StudentActivityType, UnenrolmentType } from './enums';

/**
 * Shared & Helper Schemas
 */

export const DBStudentActivityLogUnenrolmentTypeSchema = z.enum(UnenrolmentType);

/**
 * DB Schemas
 */

export const DBStudentActivityLogStudentSchema = z.lazy(() => DBStudentStudentIdSchema);
export const DBStudentActivityLogTypeDatetimeIdentifierSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 2 &&
    z.enum(StudentActivityType).safeParse(parts[0]).success &&
    UnixTimestampSchema.safeParse(Number(parts[1])).success
  );
});
export const DBStudentActivityLogActorSchema = z.lazy(() => DBStaffStaffIdSchema);
export const DBStudentActivityLogAttendedLessonsCountSchema = z.number().int().min(0);
export const DBStudentActivityLogClassSchema = z.lazy(() => DBClassSchema);
export const DBStudentActivityLogClassIdSchema = z.lazy(() => DBClassClassIdSchema);
export const DBStudentActivityLogClassSizeSchema = z.number().int().min(0);
export const DBStudentActivityLogCourseStateSchema = z.lazy(() => DBStudentCourseStateStateSchema);
export const DBStudentActivityLogCreationDatetimeSchema = UnixTimestampSchema;
export const DBStudentActivityLogReasonSchema = z.string().min(0).max(4096);
export const DBStudentActivityLogReasonCategorySchema = z.enum(StudentActivityReasonCategory);
export const DBStudentActivityLogRecentAttendedLessonsSchema = z.lazy(() =>
  z.array(DBLessonEnrolmentSchema)
);
export const DBStudentActivityLogRecentAttendedTeachersSchema = z.array(
  z.lazy(() => DBStaffStaffIdSchema)
);
export const DBStudentActivityLogStudentStateSchema = z.lazy(() => DBStudentStateSchema);
export const DBStudentActivityLogSubjectSchema = SubjectSchema;
// Diverges from DBClassTeacherSchema (which accepts UUID | '') because the
// studentActivityLog table uses `teacher` as a GSI partition key on the
// `byTeacherTypeDatetime` index, and DynamoDB forbids empty strings as GSI
// PK values. Writers at classes.entity.ts substitute '_' for unassigned
// teachers so the GSI partition is always non-empty.
export const DBStudentActivityLogTeacherSchema = z.union([
  z.lazy(() => DBStaffStaffIdSchema),
  z.literal('_'),
]);
export const DBStudentActivityLogTotalLessonsCountSchema = z.number().int().min(0);

export const DBStudentActivityLogSchema = z.object({
  student: DBStudentActivityLogStudentSchema,
  typeDatetimeIdentifier: DBStudentActivityLogTypeDatetimeIdentifierSchema,
  actor: DBStudentActivityLogActorSchema,
  creationDatetime: DBStudentActivityLogCreationDatetimeSchema,
  // Legacy records pre-date these fields being written on every log entry.
  class: DBStudentActivityLogClassSchema.optional(),
  classId: DBStudentActivityLogClassIdSchema.optional(),
  classSize: DBStudentActivityLogClassSizeSchema.optional(),
  studentState: DBStudentActivityLogStudentStateSchema.optional(),
  subject: DBStudentActivityLogSubjectSchema.optional(),
  teacher: DBStudentActivityLogTeacherSchema.optional(),
  // Optional fields - only present for certain activity types
  courseState: DBStudentActivityLogCourseStateSchema.optional(),
  reason: DBStudentActivityLogReasonSchema.optional(),
  reasonCategory: DBStudentActivityLogReasonCategorySchema.optional(),
  attendedLessonsCount: DBStudentActivityLogAttendedLessonsCountSchema.optional(),
  totalLessonsCount: DBStudentActivityLogTotalLessonsCountSchema.optional(),
  recentAttendedLessons: DBStudentActivityLogRecentAttendedLessonsSchema.optional(),
  recentAttendedTeachers: DBStudentActivityLogRecentAttendedTeachersSchema.optional(),
});

/**
 * Client Schemas
 */

export const StudentActivityLogStudentSchema = DBStudentActivityLogStudentSchema;
export const StudentActivityLogActivityTypeSchema = z.enum(StudentActivityType);
export const StudentActivityLogActorSchema = DBStudentActivityLogActorSchema;
export const StudentActivityLogAttendedLessonsCountSchema =
  DBStudentActivityLogAttendedLessonsCountSchema;
export const StudentActivityLogClassSchema = z.lazy(() =>
  z.object({
    active: z.lazy(() => ClassActiveSchema),
    allowMakeups: z.lazy(() => ClassAllowMakeupsSchema),
    classId: z.lazy(() => ClassClassIdSchema),
    course: z.lazy(() => ClassCourseSchema),
    day: z.lazy(() => ClassDaySchema),
    location: z.lazy(() => ClassLocationSchema),
    marker: z.lazy(() => ClassMarkerSchema),
    markerName: z.lazy(() => ClassMarkerNameSchema).optional(),
    notes: z.lazy(() => ClassNotesSchema),
    permissionGroup: z.lazy(() => ClassPermissionGroupSchema),
    remindBreaks: z.lazy(() => ClassRemindBreaksSchema),
    room: z.lazy(() => ClassRoomSchema),
    schoolTag: z.lazy(() => ClassSchoolTagSchema),
    slackChannelName: z.lazy(() => ClassSlackChannelNameSchema),
    slackId: z.lazy(() => ClassSlackIdSchema),
    stream: z.lazy(() => ClassStreamSchema),
    studentCap: z.lazy(() => ClassStudentCapSchema),
    studentCount: z.lazy(() => ClassStudentCountSchema),
    students: z.lazy(() => ClassStudentsSchema),
    subject: z.lazy(() => ClassSubjectSchema),
    tags: z.lazy(() => ClassTagsSchema),
    teacher: z.lazy(() => ClassTeacherSchema),
    teacherName: z.lazy(() => ClassTeacherNameSchema).optional(),
    time: z.lazy(() => ClassTimeSchema),
    waitlist: z.lazy(() => ClassWaitlistSchema),
  })
);
export const StudentActivityLogClassIdSchema = DBStudentActivityLogClassIdSchema;
export const StudentActivityLogClassSizeSchema = DBStudentActivityLogClassSizeSchema;
export const StudentActivityLogCourseStateSchema = DBStudentActivityLogCourseStateSchema;
export const StudentActivityLogCreationDatetimeSchema = DBStudentActivityLogCreationDatetimeSchema;
export const StudentActivityLogReasonSchema = DBStudentActivityLogReasonSchema;
export const StudentActivityLogReasonCategorySchema = DBStudentActivityLogReasonCategorySchema;
export const StudentActivityLogRecentAttendedLessonsSchema = z.lazy(() =>
  z.array(
    z.object({
      student: z.lazy(() => LessonEnrolmentStudentSchema),
      classId: z.lazy(() => LessonEnrolmentClassIdSchema),
      lesson: z.lazy(() => LessonEnrolmentLessonSchema),
      attendanceNotified: z.lazy(() => LessonEnrolmentAttendanceNotifiedSchema),
      attendanceType: z.lazy(() => LessonEnrolmentAttendanceTypeSchema),
      billingType: z.lazy(() => LessonEnrolmentBillingTypeSchema),
      course: z.lazy(() => LessonEnrolmentCourseSchema),
      enrolmentMethod: z.lazy(() => LessonEnrolmentEnrolmentMethodSchema),
      homework: z.lazy(() => LessonEnrolmentHomeworkSchema),
      present: z.lazy(() => LessonEnrolmentPresentSchema),
      rollMarked: z.lazy(() => LessonEnrolmentRollMarkedSchema),
      separatelyBilled: z.lazy(() => LessonEnrolmentSeparatelyBilledSchema),
      startDatetime: z.lazy(() => LessonEnrolmentStartDatetimeSchema),
      stream: z.lazy(() => LessonEnrolmentStreamSchema).optional(),
    })
  )
);
export const StudentActivityLogRecentAttendedTeachersSchema =
  DBStudentActivityLogRecentAttendedTeachersSchema;
export const StudentActivityLogStudentStateSchema = DBStudentActivityLogStudentStateSchema;
export const StudentActivityLogSubjectSchema = DBStudentActivityLogSubjectSchema;
export const StudentActivityLogTeacherSchema = DBStudentActivityLogTeacherSchema;
export const StudentActivityLogTotalLessonsCountSchema =
  DBStudentActivityLogTotalLessonsCountSchema;

export const StudentActivityLogSchema = z.object({
  student: StudentActivityLogStudentSchema,
  activityType: StudentActivityLogActivityTypeSchema,
  actor: StudentActivityLogActorSchema,
  creationDatetime: StudentActivityLogCreationDatetimeSchema,
  // Legacy records pre-date these fields being written on every log entry.
  class: StudentActivityLogClassSchema.optional(),
  classId: StudentActivityLogClassIdSchema.optional(),
  classSize: StudentActivityLogClassSizeSchema.optional(),
  studentState: StudentActivityLogStudentStateSchema.optional(),
  subject: StudentActivityLogSubjectSchema.optional(),
  teacher: StudentActivityLogTeacherSchema.optional(),
  // Optional fields - only present for certain activity types
  courseState: StudentActivityLogCourseStateSchema.optional(),
  reason: StudentActivityLogReasonSchema.optional(),
  reasonCategory: StudentActivityLogReasonCategorySchema.optional(),
  attendedLessonsCount: StudentActivityLogAttendedLessonsCountSchema.optional(),
  totalLessonsCount: StudentActivityLogTotalLessonsCountSchema.optional(),
  recentAttendedLessons: StudentActivityLogRecentAttendedLessonsSchema.optional(),
  recentAttendedTeachers: StudentActivityLogRecentAttendedTeachersSchema.optional(),
});
